import React, { useMemo } from 'react';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  AutocompleteFieldItem,
  SuggestionItem,
} from '@domains/documents-review/interfaces/interfaces';
import { robotoRegular } from '@habitech/shared';
import { SOURCE_TYPE_LABELS } from '@domains/documents-review/utils/autocompleteLabels';

interface AutocompleteAnalyticsProps {
  items: AutocompleteFieldItem[];
  totalExpectedFields?: number;
  nonFillableFieldsCount?: number;
}

const COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7f50',
  '#8dd1e1',
  '#a4de6c',
];

export const AutocompleteAnalytics: React.FC<AutocompleteAnalyticsProps> = ({
  items,
  totalExpectedFields,
  nonFillableFieldsCount = 0,
}) => {
  const {
    totalFields,
    fieldsReviewed,
    avgFeedback,
    totalSuggestions,
    avgConfidence,
    sourceTypeData,
  } = useMemo(() => {
    const totalFieldsLocal =
      typeof totalExpectedFields === 'number'
        ? totalExpectedFields
        : items.length;
    const reviewed = items.filter(i => i.reviewed).length;

    const feedbacks = items
      .map(i => (typeof i.userFeedback === 'number' ? i.userFeedback : null))
      .filter((v): v is number => v != null && !Number.isNaN(v));
    const avgFeedbackLocal = feedbacks.length
      ? Math.round(feedbacks.reduce((a, b) => a + b, 0) / feedbacks.length)
      : 0;

    const allSuggestions: SuggestionItem[] = items.flatMap(
      i => i.suggestions || []
    );
    const totalSuggestionsLocal = allSuggestions.length;
    const avgConfidenceLocal = allSuggestions.length
      ? Math.round(
          allSuggestions.reduce(
            (acc, s) => acc + (s.initialConfidence || 0),
            0
          ) / allSuggestions.length
        )
      : 0;

    const sourceTypeCount: Record<string, number> = {};
    for (const s of allSuggestions) {
      const key = s.sourceType || 'desconocido';
      sourceTypeCount[key] = (sourceTypeCount[key] || 0) + 1;
    }
    const sourceTypeDataLocal = Object.entries(sourceTypeCount).map(
      ([name, value]) => ({ name: SOURCE_TYPE_LABELS[name] || name, value })
    );

    return {
      totalFields: totalFieldsLocal,
      fieldsReviewed: reviewed,
      avgFeedback: avgFeedbackLocal,
      totalSuggestions: totalSuggestionsLocal,
      avgConfidence: avgConfidenceLocal,
      sourceTypeData: sourceTypeDataLocal,
    };
  }, [items]);

  const reviewedPct = totalFields
    ? Math.round((fieldsReviewed / totalFields) * 100)
    : 0;

  const nonFillable = Math.max(nonFillableFieldsCount, 0);
  const pendingFillable = Math.max(
    totalFields - fieldsReviewed - nonFillable,
    0
  );

  return (
    <div data-testid="autocomplete-analytics" className="mb-3">
      <Row className="g-3">
        <Col sm={6} md={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="card-title">Campos totales</div>
              <div className="fs-5 fw-bold" data-testid="metric-total-fields">
                {totalFields}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="card-title">Campos revisados</div>
              <div
                className="fs-5 fw-bold d-flex align-items-center gap-2"
                data-testid="metric-fields-reviewed"
              >
                {fieldsReviewed}{' '}
                <span className="text-muted fs-6">({reviewedPct}%)</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="card-title">Feedback promedio</div>
              <div className="fs-5 fw-bold" data-testid="metric-avg-feedback">
                {avgFeedback}%
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} md={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="card-title">Confianza promedio</div>
              <div className="fs-5 fw-bold" data-testid="metric-avg-confidence">
                {avgConfidence}%
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={8}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Card.Title className="mb-0">
                  Distribución por tipo de fuente
                </Card.Title>
                <div className="card-title mb-0">
                  Total sugerencias:{' '}
                  <strong data-testid="metric-total-suggestions">
                    {totalSuggestions}
                  </strong>
                </div>
              </div>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={sourceTypeData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                  >
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 14, fontFamily: robotoRegular }}
                    />
                    <Bar dataKey="value">
                      {sourceTypeData.map((entry, index) => (
                        <Cell
                          key={`cell-${entry.name}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title>Avance de revisión</Card.Title>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      dataKey="value"
                      data={[
                        { name: 'Revisado', value: fieldsReviewed },
                        { name: 'Pendiente', value: pendingFillable },
                        { name: 'Sin sugerencia', value: nonFillable },
                      ]}
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      label
                    >
                      <Cell fill={COLORS[1]} />
                      <Cell fill={COLORS[2]} />
                      <Cell fill="#ff4d4f" />
                    </Pie>
                    <Tooltip
                      contentStyle={{ fontSize: 14, fontFamily: robotoRegular }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
